import { useCallback, useContext, useEffect, useReducer, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { useDialog } from "@datanac/datanac-mui-toolkit";
import { Box, Checkbox, FormControl, FormControlLabel, Grid } from "@mui/material";
import { UsersApiHelper as UsersApiHelperForEquipment, UsersApiHelperForMember } from "api/ApiHelper";
import { AppContext } from "App";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { MASK_NUMERIC, StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { TilleyDatePicker } from "components/Core/TilleyDatePicker";
import { FinancialIcon } from "components/Icons/Icons";
import { useBlockerPrompt } from "components/Menu/hooks/useBlockerPrompt";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { SkeletonWrapper } from "mui-toolkit/SkeletonWrapper";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";

export default function DebtScheduleAmortizationWorkspace() {
  const { globalState } = useContext(AppContext);
  const { debt_schedule_id, amortization_id, equipment_id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { component: confirmDialogComponent } = useDialog();
  const { component: formStatusComponent, actions: formStatusActions, state: formStatusState } = useFormStatus();

  const [isLoading, setIsLoading] = useState(true);
  const [state, dispatch] = useReducer(propUpdateReducer, { is_active: true });
  const [debtSchedule, setDebtSchedule] = useState({});
  const [formErrors, setFormErrors] = useState({});

  const api = UsersApiHelperForMember;

  const handleError = useCallback(
    (err) => {
      console.warn(err);
      const errorMessage = err.detail || err.message || "";
      if (errorMessage.includes("UX_DebtScheduleAmortization_DebtSchedule_PaymentNumber")) {
        enqueueSnackbar("Amortization with this payment number already exists.", {
          preventDuplicate: true,
          variant: "error",
        });
        setFormErrors({ payment_number: "Already exists" });
      } else {
        enqueueSnackbar(errorMessage || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
      }
    },
    [enqueueSnackbar]
  );

  const [equipment, setEquipment] = useState({});
  useEffect(() => {
    if (equipment_id) {
      loadEquipmentData();
    }
  }, [equipment_id]);
  const loadEquipmentData = async () => {
    try {
      const equipmentList = await UsersApiHelperForEquipment.selectEquipmentInventory(
        {
          producer_token: globalState.producer_token,
          year: globalState.crop_year,
          is_active: true,
          id: equipment_id,
        },
        globalState
      );
      const foundEquipment = Array.isArray(equipmentList)
        ? equipmentList.find((eq) => eq.id === equipment_id) || null
        : null;
      setEquipment(foundEquipment);
    } catch (err) {
      console.warn(err);
      enqueueSnackbar(err.detail || err.message || "Error loading equipment", {
        preventDuplicate: true,
        variant: "error",
      });
    }
  };

  const loadDebtSchedule = useCallback(async () => {
    try {
      const debtSchedules = await api.callPost(
        "debtSchedule/Search",
        {
          id: debt_schedule_id,
          producer_token: globalState?.producer_token,
          is_active: true,
        },
        globalState
      );
      if (debtSchedules?.length) {
        setDebtSchedule(debtSchedules[0]);
      }
    } catch (err) {
      handleError(err);
    }
  }, [debt_schedule_id, globalState?.producer_token, handleError]);

  useEffect(() => {
    loadDebtSchedule();
  }, [loadDebtSchedule]);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.callPost(
        "DebtScheduleAmortization/search",
        {
          producer_token: globalState.producer_token,
          id: amortization_id,
        },
        globalState
      );
      if (response?.length) {
        dispatch({
          type: "updateMany",
          payload: response[0],
        });
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, [amortization_id, globalState?.producer_token, handleError]);

  useEffect(() => {
    if (String(amortization_id) === "0") {
      dispatch({
        type: "updateMany",
        payload: {
          id: uuidv4(),
          debt_schedule_id: debt_schedule_id,
          is_active: true,
        },
      });
      setIsLoading(false);
    } else {
      loadData();
    }
  }, [amortization_id, debt_schedule_id, loadData]);

  const isValid = useCallback(() => {
    if (!state?.payment_number || !state?.payment_date) {
      return false;
    }

    if (state?.actual_payment_date === "") {
      return false;
    }

    return true;
  }, [state]);

  useBlockerPrompt(!isValid(), "DebtScheduleAmortizationWorkspace");

  useEffect(() => {
    const updateAmortization = async () => {
      try {
        formStatusActions.setIsFormDirty(true, true);
        await api.callPost(
          "DebtScheduleAmortization",
          {
            DebtScheduleAmortization: [{ ...state, producer_token: globalState.producer_token }],
            producer_token: globalState.producer_token,
          },
          globalState
        );
        formStatusActions.setIsSaving(false);

        if (amortization_id === "0") {
          if (equipment_id) {
            navigate(`/financial/equipment/${equipment_id}/${debt_schedule_id}/${state.id}`, { replace: true });
          } else {
            navigate(`/financial/debt_schedule/${debt_schedule_id}/${state.id}`, { replace: true });
          }
        }
      } catch (err) {
        formStatusActions.setIsFormDirty(true, false);
        handleError(err);
      }
    };

    if (isValid() && globalState?.producer_token && formStatusState?.isFormDirty) {
      updateAmortization();
    }
  }, [state, equipment_id, debt_schedule_id]);

  const handleChangeState = (event) => {
    const value = event.target.value;
    if (state[event.target.name] !== value) {
      if (event.target.name === "payment_number") {
        setFormErrors((prev) => ({ ...prev, payment_number: undefined }));
      }
      formStatusActions.setIsFormDirty(true, false);
      dispatch({
        type: "update",
        payload: { key: event.target.name, value: value },
      });
    }
  };

  const handleChangeIsActual = (event) => {
    const value = event.target.checked;
    if (state.is_actual !== value) {
      formStatusActions.setIsFormDirty(true, false);
      dispatch({
        type: "update",
        payload: { key: "is_actual", value },
      });
    }
  };

  const handleDateChange = (event) => {
    const fieldName = event.target.name;
    const value = event.target.value;

    if (state[fieldName] !== value) {
      formStatusActions.setIsFormDirty(true, false);
      dispatch({
        type: "updateMany",
        payload: { [fieldName]: value },
      });
    }
  };

  const formMaxWidth = "1200px";

  return (
    <>
      {equipment_id ? (
        <WorkspaceHeader
          title={amortization_id === "0" ? "Amortization" : `Payment #${state?.payment_number || "..."}`}
          category={{
            title: "Financial Analysis",
            icon: <FinancialIcon />,
          }}
          breadCrumbs={[
            { title: "Equipment Inventory", path: "/financial/equipment" },
            { title: equipment?.name || "...", path: `/financial/equipment/${equipment_id}` },
            {
              title: debtSchedule?.creditor || "...",
              path: `/financial/equipment/${equipment_id}/${debt_schedule_id}`,
            },
          ]}
        />
      ) : (
        <WorkspaceHeader
          title={amortization_id === "0" ? "Amortization" : `Payment #${state?.payment_number || "..."}`}
          category={{
            title: "Financial Analysis",
            icon: <FinancialIcon />,
          }}
          breadCrumbs={[
            { title: "Debt Schedule", path: "/financial/debt_schedule" },
            {
              title: debtSchedule?.creditor || "...",
              path: `/financial/debt_schedule/${debt_schedule_id}`,
            },
          ]}
        />
      )}
      <Box sx={{ width: "100%", maxWidth: formMaxWidth, minHeight: "6in" }}>
        <WorkspacePageHeader title="Amortization" additionalControls={formStatusComponent} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Payment Number"
                name="payment_number"
                isRequired
                isLoading={isLoading}
                defaultValue={state.payment_number || ""}
                onChange={handleChangeState}
                maskType={MASK_NUMERIC}
                errorText={formErrors.payment_number}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TilleyDatePicker
              name="payment_date"
              label="Payment Date"
              value={state?.payment_date}
              onChange={handleDateChange}
              isLoading={isLoading}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Beginning Balance"
                name="beginning_balance"
                isLoading={isLoading}
                defaultValue={state.beginning_balance || ""}
                onChange={handleChangeState}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Payment Amount"
                name="payment_amount"
                isLoading={isLoading}
                defaultValue={state.payment_amount || ""}
                onChange={handleChangeState}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Principal Payment"
                name="principal_payment"
                isLoading={isLoading}
                defaultValue={state.principal_payment || ""}
                onChange={handleChangeState}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Interest Payment"
                name="interest_payment"
                isLoading={isLoading}
                defaultValue={state.interest_payment || ""}
                onChange={handleChangeState}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Ending Balance"
                name="ending_balance"
                isLoading={isLoading}
                defaultValue={state.ending_balance || ""}
                onChange={handleChangeState}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Cumulative Principal"
                name="cumulative_principal"
                isLoading={isLoading}
                defaultValue={state.cumulative_principal || ""}
                onChange={handleChangeState}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Cumulative Interest"
                name="cumulative_interest"
                isLoading={isLoading}
                defaultValue={state.cumulative_interest || ""}
                onChange={handleChangeState}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} lg={9}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Notes"
                name="notes"
                isLoading={isLoading}
                defaultValue={state.notes || ""}
                onChange={handleChangeState}
                fullWidth
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <SkeletonWrapper isLoading={isLoading}>
              <FormControlLabel
                key="is_actual"
                control={
                  <Checkbox name="is_actual" checked={state?.is_actual ?? false} onChange={handleChangeIsActual} />
                }
                label="Is Actual"
                sx={{
                  marginTop: 0,
                }}
              />
            </SkeletonWrapper>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TilleyDatePicker
              name="actual_payment_date"
              label="Actual Payment Date"
              value={state?.actual_payment_date}
              onChange={handleDateChange}
              isLoading={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Actual Payment Amount"
                name="actual_payment_amount"
                isLoading={isLoading}
                defaultValue={state.actual_payment_amount || ""}
                onChange={handleChangeState}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Extra Principal Payment"
                name="extra_principal_payment"
                isLoading={isLoading}
                defaultValue={state.extra_principal_payment || ""}
                onChange={handleChangeState}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Extra Interest Payment"
                name="extra_interest_payment"
                isLoading={isLoading}
                defaultValue={state.extra_interest_payment || ""}
                onChange={handleChangeState}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Box>
      {confirmDialogComponent}
    </>
  );
}
